import { Add, ContentCopy, Delete } from '@mui/icons-material';
import {
    Alert, Box, Button, Card, CardContent, Chip, Container, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, FormControl, IconButton, InputLabel,
    MenuItem, Select, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_KEY_CONFIG, PRICING } from '@/config/app.config';
import type { PlanId } from '@/domain/entities';
import { ApiError } from '@/infrastructure/services/api-client';
import { useApiKeysStore } from '@/stores/use-api-keys-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

export function ApiKeysPage() {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const { keys, fetchKeys, generateKey, revokeKey, getActiveKeys } = useApiKeysStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [generateOpen, setGenerateOpen] = useState(false);
    const [revokeOpen, setRevokeOpen] = useState<string | null>(null);
    const [newKeyName, setNewKeyName] = useState('');
    const [selectedPlanId, setSelectedPlanId] = useState<PlanId | ''>('');
    const [justCreatedKey, setJustCreatedKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        void fetchKeys();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) {
        return null;
    }

    const activeKeys = getActiveKeys();
    const canGenerate = activeKeys.length < API_KEY_CONFIG.maxKeysPerUser;

    const handleGenerate = async () => {
        if (!newKeyName.trim() || !selectedPlanId) {
            return;
        }
        setLoading(true);
        try {
            const fullKey = await generateKey(newKeyName.trim(), selectedPlanId);
            setJustCreatedKey(fullKey);
            setNewKeyName('');
            setSelectedPlanId('');
            setGenerateOpen(false);
            addNotification(t('notifications.keyGenerated'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async (keyId: string) => {
        try {
            await revokeKey(keyId);
            setRevokeOpen(null);
            addNotification(t('notifications.keyRevoked'), 'warning');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        }
    };

    const handleCopy = (text: string) => {
        void navigator.clipboard.writeText(text);
        addNotification(t('common.copied'), 'success');
    };

    const formatDate = (date: Date) => {
        return new Date(date).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    // Available plans for key generation (only subscribed plans)
    const availablePlans = user.subscriptions
        .map((planId) => {
            const plan = planId === 'mcp-abap' ? PRICING.plans.abap : PRICING.plans.btp;
            return { id: planId as PlanId, name: plan.name };
        });

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {t('apiKeys.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('apiKeys.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setGenerateOpen(true)}
                    disabled={!canGenerate || availablePlans.length === 0}
                    sx={{ borderRadius: 3, fontWeight: 600 }}
                >
                    {t('apiKeys.generate')}
                </Button>
            </Box>

            {!canGenerate && (
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
                    {t('apiKeys.maxKeys', { max: API_KEY_CONFIG.maxKeysPerUser })}
                </Alert>
            )}

            {availablePlans.length === 0 && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
                    {t('dashboard.noPlans')}
                </Alert>
            )}

            {justCreatedKey && (
                <Alert
                    severity="info"
                    sx={{ mb: 3, borderRadius: 3 }}
                    action={
                        <IconButton size="small" onClick={() => handleCopy(justCreatedKey)}>
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    }
                    onClose={() => setJustCreatedKey(null)}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t('apiKeys.copyWarning')}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
                    >
                        {justCreatedKey}
                    </Typography>
                </Alert>
            )}

            <Card
                sx={{
                    border: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    {keys.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">{t('common.noData')}</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('apiKeys.name')}</TableCell>
                                        <TableCell>{t('apiKeys.key')}</TableCell>
                                        <TableCell>{t('apiKeys.plan')}</TableCell>
                                        <TableCell>{t('apiKeys.status')}</TableCell>
                                        <TableCell>{t('apiKeys.createdAt')}</TableCell>
                                        <TableCell align="right">{t('common.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {keys.map((apiKey) => (
                                        <TableRow key={apiKey.id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {apiKey.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontFamily: '"JetBrains Mono", monospace',
                                                        fontSize: '0.75rem',
                                                    }}
                                                >
                                                    {apiKey.maskedKey}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={apiKey.planId === 'mcp-abap' ? PRICING.plans.abap.name : PRICING.plans.btp.name}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={apiKey.status === 'active' ? t('apiKeys.active') : t('apiKeys.revoked')}
                                                    color={apiKey.status === 'active' ? 'success' : 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(apiKey.createdAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {apiKey.status === 'active' && (
                                                    <Tooltip title={t('apiKeys.revoke')}>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            onClick={() => setRevokeOpen(apiKey.id)}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Generate Dialog */}
            <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>{t('apiKeys.generate')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t('apiKeys.name')}
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder={t('apiKeys.namePlaceholder')}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel>{t('apiKeys.plan') || 'Plan'}</InputLabel>
                        <Select
                            value={selectedPlanId}
                            label={t('apiKeys.plan') || 'Plan'}
                            onChange={(e) => setSelectedPlanId(e.target.value as PlanId)}
                        >
                            {availablePlans.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                    {plan.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setGenerateOpen(false)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerate}
                        disabled={!newKeyName.trim() || !selectedPlanId || loading}
                    >
                        {loading ? t('common.loading') : t('apiKeys.generate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirmation Dialog */}
            <Dialog open={!!revokeOpen} onClose={() => setRevokeOpen(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>{t('apiKeys.revokeConfirm')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('apiKeys.revokeWarning')}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRevokeOpen(null)}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={() => revokeOpen && handleRevoke(revokeOpen)}>
                        {t('apiKeys.revoke')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ApiKeysPage;
